import React, { useState, useEffect } from 'react';
import { pwaService } from '../services/pwaService';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const PWAInstallBanner: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check initial installation status
    const checkInstallStatus = () => {
      const deviceInfo = pwaService.getDeviceInfo();
      setIsInstalled(deviceInfo.isInstalled);
      
      if (deviceInfo.isInstalled) {
        setShowBanner(false);
        return;
      }
    };

    // Check immediately
    checkInstallStatus();

    // Listen for install prompt changes
    const unsubscribe = pwaService.onInstallPromptChange((prompt) => {
      setDeferredPrompt(prompt);
      
      if (prompt) {
        // Get fresh device info and check if banner should be shown using unified method
        const deviceInfo = pwaService.getDeviceInfo();
        setIsInstalled(deviceInfo.isInstalled);
        
        if (!deviceInfo.isInstalled) {
          const { showBanner: shouldShow } = pwaService.shouldShowAnyPrompt();
          console.log('[PWAInstallBanner] Device info:', deviceInfo);
          console.log('[PWAInstallBanner] Should show banner:', shouldShow);
          setShowBanner(shouldShow);
        } else {
          setShowBanner(false);
        }
      } else {
        setShowBanner(false);
      }
    });

    // Check periodically for installation status changes
    const interval = setInterval(checkInstallStatus, 1000);

    return () => {
      unsubscribe();
      clearInterval(interval);
    };
  }, []);

  const handleInstall = async () => {
    const success = await pwaService.install();
    if (success) {
      setShowBanner(false);
    }
  };

  const handleClose = () => {
    setShowBanner(false);
    pwaService.markBannerClosed();
  };

  // 설치된 경우 또는 배너를 표시하지 않는 경우
  if (isInstalled || !showBanner || !deferredPrompt) {
    return null;
  }

  return (
    <div className="scanvcf-sticky-banner fixed bottom-0 left-0 right-0 bg-blue-600 text-white p-4 shadow-lg z-50 flex items-center justify-between">
      <div className="flex items-center space-x-3">
        <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
          <img
            src="/icon-32x32.png"
            alt="ScanVCF"
            className="w-8 h-8"
          />
        </div>
        <div>
          <h3 className="font-semibold text-sm">Install ScanVCF</h3>
          <p className="text-xs opacity-90">Get quick access to your business cards</p>
        </div>
      </div>
      
      <div className="flex items-center space-x-2">
        <button
          onClick={handleInstall}
          className="bg-white text-blue-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors"
        >
          Install
        </button>
        <button
          onClick={handleClose}
          className="text-white hover:text-gray-200 p-1"
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default PWAInstallBanner;
